import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrganizationUser, clubsBookingApi } from '@/api/clubs-booking';
import AdminMobileRowActions from '@/components/admin/AdminMobileRowActions';
import {
  AdminDataTable,
  AdminSection,
  adminPrimaryButtonClassName,
  adminTableCellClassName,
  adminTableClassName,
  adminTableHeadCellClassName,
  adminToolbarClassName,
} from '@/components/admin/AdminLayout';
import AdminShell from '@/components/admin/AdminShell';
import TablePagination from '@/components/common/TablePagination';

const UsersPage = () => {
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await clubsBookingApi.listOrganizationUsers();
      setUsers(data || []);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setError('Users API is unavailable on this backend (missing GET /v1/auth/users). Restart/update the API service.');
        setUsers([]);
        return;
      }

      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return users.slice(start, start + pageSize);
  }, [users, page, pageSize]);

  return (
    <AdminShell title="Users" subtitle="Manage your team and choose what level of access each person has">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <AdminSection>
        <div className={adminToolbarClassName}>
          <h2 className="text-2xl font-semibold">Users list</h2>
          <Link to="/users/new" className={adminPrimaryButtonClassName}>
            Add user
          </Link>
        </div>

        <AdminDataTable>
          <table className={`${adminTableClassName} min-w-[640px]`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className={adminTableHeadCellClassName}>Name</th>
                <th className={adminTableHeadCellClassName}>Email</th>
                <th className={adminTableHeadCellClassName}>Role</th>
                <th className={adminTableHeadCellClassName}>Created</th>
                <th className={`${adminTableHeadCellClassName} hidden sm:table-cell`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pagedUsers.map((user) => (
                <tr key={user.id}>
                  <td className={`${adminTableCellClassName} font-medium`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {user.name}
                        {user.isOrganizationOwner ? (
                          <span className="ml-2 rounded-full bg-[#ECF8F3] px-2 py-1 text-xs font-medium text-[#1F8F5F]">
                            Owner
                          </span>
                        ) : null}
                      </div>
                      {!user.isOrganizationOwner ? (
                        <AdminMobileRowActions
                          title={user.name}
                          description="Manage this team member."
                          items={[
                            { label: 'Edit user', to: `/users/${user.id}/edit` },
                          ]}
                        />
                      ) : null}
                    </div>
                  </td>
                  <td className={`${adminTableCellClassName} text-slate-700`}>{user.email}</td>
                  <td className={`${adminTableCellClassName} capitalize`}>{user.isOrganizationOwner ? 'Owner' : user.role}</td>
                  <td className={`${adminTableCellClassName} text-slate-600`}>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB') : '-'}
                  </td>
                  <td className={`${adminTableCellClassName} hidden sm:table-cell`}>
                    {!user.isOrganizationOwner ? (
                      <Link to={`/users/${user.id}/edit`} className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50">
                        Edit
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400">Owner account</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && users.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No users found.</p>}
          {loading && <p className="px-4 py-6 text-sm text-slate-500">Loading users...</p>}
          {!loading && users.length > 0 && (
            <TablePagination
              totalItems={users.length}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </AdminDataTable>
      </AdminSection>
    </AdminShell>
  );
};

export default UsersPage;



